import React, { FC, useState, ChangeEvent } from 'react'
import { ReactComponent as EmailLinkIllustration } from 'assets/illustrations/Email.svg'
import { Button } from 'transfix-ui/components/Button'
import { INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui/components/InlineMessage'
import { TextInput } from 'components/atoms'
import { validateEmail } from 'utils/validators'

interface ErrorType {
  type: INLINE_MESSAGE_VARIANTS
  message: string
}

interface IRequestLoginLinkFormProps {
  onSubmitLink: (email?: string) => Promise<void>
  backToSignupUrl: string
  onClickBackToSignup?: () => void
  isResetPasswordRequest?: boolean
}

export const RequestLoginLinkForm: FC<IRequestLoginLinkFormProps> = ({
  backToSignupUrl,
  isResetPasswordRequest,
  onClickBackToSignup,
  onSubmitLink,
}) => {
  const [error, setError] = useState<ErrorType>()
  const [email, setEmail] = useState<string>()
  const [emailSent, setEmailSent] = useState(false)

  const emailChangeHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
    setEmail(value)

  const resetFormHandler = () => {
    setEmailSent(false)
  }

  const submitLinkRequestHandler = async () => {
    try {
      if (!validateEmail(email)) {
        setError({
          type: INLINE_MESSAGE_VARIANTS.WARNING,
          message: 'Whoops, the email provided is invalid.',
        })
      } else {
        setError(undefined)
        onSubmitLink(email)
        setEmailSent(true)
      }
    } catch (e) {
      const message = 'Something went wrong. Please try again.'
      setError({ type: INLINE_MESSAGE_VARIANTS.ALERT, message })
    }
  }

  return !emailSent ? (
    <form onSubmit={submitLinkRequestHandler}>
      <h2 className='mb4 tc'>
        {isResetPasswordRequest ? 'Reset my Password' : 'Get a Login Link'}
      </h2>
      <p className='mb5 tc'>
        Enter the email address you used to create your account and you will receive an email with
        further instructions.
      </p>
      {error && (
        <InlineMessage className='mb5' fullWidth message={error.message} type={error.type} />
      )}
      <div className='flex-grow-1 mb4'>
        <TextInput
          autoComplete='email'
          label='Email'
          large
          value={email}
          onChange={emailChangeHandler}
          dataTest='email-input'
        />
      </div>
      <div className='flex-grow-1 mb5'>
        <Button
          fullWidth
          color='primary'
          onClick={submitLinkRequestHandler}
          size='large'
          data-test='request-link-submit'>
          {isResetPasswordRequest ? 'Request A Reset Password Link' : 'Send Login Link'}
        </Button>
      </div>
      <p className='tc'>
        <a className='b' href={backToSignupUrl} onClick={onClickBackToSignup}>
          Back to Log In
        </a>
      </p>
    </form>
  ) : (
    <>
      <p className='mb5 tc'>
        <EmailLinkIllustration />
      </p>
      <h2 className='mb4 tc'>
        {isResetPasswordRequest
          ? 'Check your email for a reset password link'
          : 'Check your email for a login link'}
      </h2>
      <p className='mb5 tc'>
        If you created an account with this email address, you should receive a link with further
        instructions.
      </p>
      <p className='tc'>
        Didn’t receive an email? Please{' '}
        <Button className='btn-text-link-b' onClick={resetFormHandler}>
          re-enter your email address.
        </Button>
      </p>
    </>
  )
}
